/* eslint-disable max-lines */
import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { ErrorBoundary, Layer } from 'bernie-view';
import { QueryRoute, updateSearch } from 'bernie-core';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { LocalizedComponentProps, withLocalization } from '@shared-ui/localization-context';

import { evaluateAndLogExperiment } from 'stores/utils';
import { MultiItemStore } from 'stores/multi-item-store';

import { ESSAdapter, TypeaheadRenderType, UitkTypeahead } from 'uitk-react-typeahead';
import { UitkLayoutGridItem } from 'uitk-react-layout-grid';

import { EssClientLogger } from 'typeahead-client-logger';
import { FormFieldBinding } from './form-field-binding';
import { getGridItemProps } from '../grid-item-props';
import { SearchFormFieldProps } from 'src/common/components/common/search-form';

export interface LocationProps extends LocalizedComponentProps, SearchFormFieldProps, RouteComponentProps {
  analytics?: AnalyticsStore;
  context?: ContextStore;
  experiment?: ExperimentStore;
  page?: PageStore;
  essAnalyticsLogger?: EssClientLogger;
  multiItem?: MultiItemStore;
  maxTypeaheadResults?: number;
}

export const RESET_FIELDS = ['poi', 'hotelName', 'group', 'neighborhood']; // fields to reset on location change
export const MULTI_ITEM_REGION_TYPE = 991;
export const MULTI_ITEM_REGION_TYPE_NO_HLR = 735;

// @ts-ignore
@withLocalization
@withRouter
@FormFieldBinding
@inject('analytics', 'context', 'multiItem', 'experiment', 'page')
export class Location extends React.Component<LocationProps, Record<string, unknown>> {
  private pristine = true;
  private fieldRef: React.RefObject<any>;
  private timestamp = this.getCurrentTimestampInSeconds();

  constructor(props: Readonly<LocationProps>) {
    super(props);

    this.dismissTypeahead = this.dismissTypeahead.bind(this);
    this.openTypeahead = this.openTypeahead.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
    this.validate = this.validate.bind(this);
    this.formValidator = this.formValidator.bind(this);
    this.renderTypeaheadDialog = this.renderTypeaheadDialog.bind(this);
    this.fieldRef = React.createRef();
    props.registerValidator && props.registerValidator(this.formValidator);
  }

  public renderTypeaheadDialog(children: React.ReactNode) {
    return (
      <QueryRoute query={{ pwaDialog: 'typeahead' }}>
        <Layer id="home-typeahead" onDismiss={this.dismissTypeahead}>
          {children}
        </Layer>
      </QueryRoute>
    );
  }

  public dismissTypeahead(renderType: TypeaheadRenderType) {
    const { analytics, history } = this.props;
    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent('DismissDestinationTypeahead', 'Destination Typeahead Modal Dismissed', null);
    }
    if (renderType === TypeaheadRenderType.DIALOG) {
      history.goBack();
    }
    if (this.fieldRef && this.fieldRef.current && typeof this.fieldRef.current.focusTypeaheadInput === 'function') {
      this.fieldRef.current.focusTypeaheadInput();
    }
  }

  public openTypeahead(renderType: TypeaheadRenderType) {
    const { analytics, history, location } = this.props;

    if (renderType === TypeaheadRenderType.DIALOG) {
      updateSearch({ history, location, newParams: { pwaDialog: 'typeahead' } });
    }

    if (analytics && typeof analytics.trackEvent === 'function') {
      analytics.trackEvent(':ChangeDestination', 'User searching Destination', null);
    }
  }

  public handleUpdate() {
    return (selection: { data: any; term: any }) => {
      const { updateModel, l10n, multiItem } = this.props;
      const { formatDate } = l10n;

      const { data, term } = selection;
      const userHistory = data && data.category === 'USERHISTORY';
      const isMultiItem = multiItem && multiItem.isPackageShoppingPath();
      const regionId = data?.regionId ?? '';
      const multiCityRegionId = data?.hierarchyInfo?.airport?.multicity ?? '';
      const ttlaValue = data?.hierarchyInfo?.airport?.airportCode ?? '';
      this.timestamp = this.getCurrentTimestampInSeconds();

      if (typeof updateModel === 'function') {
        this.pristine = false;
        updateModel({
          target: {
            name: 'destination',
            value: term,
            track: true,
            userHistory,
          },
        });

        updateModel({
          target: {
            name: 'latLong',
            value: data && data.location ? `${data.location.lat},${data.location.long}` : '',
          },
        });

        updateModel({
          target: {
            name: 'regionId',
            value: isMultiItem && !regionId ? multiCityRegionId : regionId,
          },
        });

        if (ttlaValue && isMultiItem) {
          multiItem?.setTtla(ttlaValue);
        }

        updateModel({
          target: {
            name: 'selected',
            value: data && data.selected ? data.selected : '',
          },
        });
        if (data && data.startDate && data.endDate) {
          updateModel({
            target: {
              name: 'startDate',
              value: formatDate(new Date(data.startDate), { raw: 'yyyy-MM-dd' }),
            },
          });
          updateModel({
            target: {
              name: 'endDate',
              value: formatDate(new Date(data.endDate), { raw: 'yyyy-MM-dd' }),
            },
          });
        }

        RESET_FIELDS.forEach(name =>
          updateModel({
            target: {
              name,
              value: undefined,
            },
          })
        );
      }
    };
  }

  static defaultProps: Partial<LocationProps> = {
    maxTypeaheadResults: 10,
  };

  public render() {
    const { context, l10n, model, multiItem, maxTypeaheadResults, experiment, page } = this.props;
    const { formatText } = l10n;
    const destination = formatText('hotels.search.form.destination.label');
    const isMultiItem = multiItem && multiItem.isPackageShoppingPath();
    const isMultiRegionDisabled =
      isMultiItem && evaluateAndLogExperiment('PWA_Packages_Exclude_MultiRegion_Destination_Search', context, experiment, page) === 1;

    const defaultEssAdapterConfig = {
      client: 'Hotels.Search',
      dest: true,
      essLogger: this.props.essAnalyticsLogger,
      expuserid: context && context.user && context.user.expUserId,
      features: 'ta_hierarchy|postal_code|contextual_ta|google|consistent_display',
      guid: context?.deviceId ?? '',
      inputId: 'hotels-destination',
      lob: 'HOTELS',
      locale: context?.locale ?? '',
      maxResults: maxTypeaheadResults,
      personalize: true,
      regionType: 2047,
      siteId: (context?.site?.id || null)!,
      lastSearchLatLong: model?.latLong,
      lastSearchRegionId: model?.regionId,
      lastSearchElapseTime: this.timestamp ? this.getCurrentTimestampInSeconds() - this.timestamp : undefined,
    };

    const multiItemEssAdapterConfig = {
      ...defaultEssAdapterConfig,
      ...{
        client: 'Packages.Search',
        features: 'nearby_airport|ta_hierarchy|consistent_display',
        lob: 'PACKAGES',
        subLob: isMultiItem ? multiItem?.multiItemContext?.packageType : '',
        personalize: false,
        regionType: model?.typeaheadRegionType || (isMultiRegionDisabled ? MULTI_ITEM_REGION_TYPE_NO_HLR : MULTI_ITEM_REGION_TYPE),
        packageType: isMultiItem ? multiItem?.multiItemContext?.packageType : '',
      },
    };

    const defaultTypeaheadProps = {
      allowExactMatch: true,
      label: destination,
      icon: 'place',
      invalid: this.validate() && this.validate()?.text,
      name: 'destination',
      onClick: this.openTypeahead,
      onDismiss: this.dismissTypeahead,
      renderDialog: this.renderTypeaheadDialog,
      renderType: TypeaheadRenderType.ADAPTIVE,
      stid: 'hotels-destination',
      value: model && model.destination,
      ref: this.fieldRef,
      onUpdate: this.handleUpdate(),
    };

    const adapter = isMultiItem
      ? new ESSAdapter({ ...multiItemEssAdapterConfig, maxResults: multiItemEssAdapterConfig.maxResults || 0 })
      : new ESSAdapter({ ...defaultEssAdapterConfig, maxResults: defaultEssAdapterConfig.maxResults || 0 });
    const essMapboxAdapterFeatures = defaultEssAdapterConfig.features.replace('google', 'mapbox');
    const mapboxFallbackEssAdapter = new ESSAdapter({
      ...adapter.config,
      features: essMapboxAdapterFeatures,
    });

    return (
      <ErrorBoundary label="location.typeahead">
        <UitkLayoutGridItem {...getGridItemProps('location', multiItem)}>
          <div>
            <Experiment name="COMET_switch_to_Mapbox_for_Maps_and_EGTA">
              <ExperimentControl>
                <UitkTypeahead {...defaultTypeaheadProps} adapter={adapter} />
              </ExperimentControl>
              <ExperimentVariant bucket={1}>
                <UitkTypeahead {...defaultTypeaheadProps} adapter={mapboxFallbackEssAdapter} />
              </ExperimentVariant>
            </Experiment>
          </div>
        </UitkLayoutGridItem>
      </ErrorBoundary>
    );
  }

  private getCurrentTimestampInSeconds() {
    return Math.floor(Date.now() / 1000);
  }

  private formValidator() {
    this.pristine = false;

    return this.validate();
  }

  private validate() {
    const { l10n, model } = this.props;
    const { formatText } = l10n;

    if (this.pristine) {
      return;
    }

    const destination = model && model.destination;
    const focus =
      this.fieldRef &&
      this.fieldRef.current &&
      typeof this.fieldRef.current.focusTypeaheadInput === 'function' &&
      this.fieldRef.current.focusTypeaheadInput;

    if (!destination) {
      return {
        text: formatText('hotels.search.validation.invalidDestination.text'),
        focus,
      };
    }
  }
}
